import { motion } from "framer-motion";
import { BadgeCheck, ArrowUpRight } from "lucide-react";

const STATS = [
  ["10+", "Years running multi-state portfolios"],
  ["78", "Communities across 16 states"],
  ["6,000+", "Units & lots overseen"],
  ["12%", "Month-over-month NOI lift"],
];

const TIMELINE = [
  { when: "Present", role: "Director of Training & Development", org: "Lasso Capital", note: "Leads training across a 50-community, 13-state portfolio — the programs distilled into Opera360 TRAIN.", color: "#FF5722" },
  { when: "2025 — Present", role: "Regional Director", org: "brightSTAR Communities", note: "28 manufactured-home communities, 3 states. Owns fair-housing training and policy infrastructure.", color: "#2563EB" },
  { when: "2023 — 2025", role: "General Manager", org: "Inspire Communities", note: "1,700 lots across 5 Las Vegas communities. Cut delinquency 2%, lifted occupancy 5% MoM.", color: "#10B981" },
  { when: "2022 — 2023", role: "Regional Asset Manager", org: "PMG · The Robinson Group", note: "2,122 units, 12 properties. Designed portfolio-wide training standards; +12% NOI MoM.", color: "#F59E0B" },
  { when: "2018 — 2021", role: "General Manager", org: "Lakeshore Management", note: "1,200 lots, 3 communities. A 19 five-star review run in 5 months.", color: "#EC4899" },
  { when: "2013 — 2017", role: "Regional Property Manager", org: "Rait Residential", note: "852 units, 36 employees. Built the '10 Pledges of Customer Service'.", color: "#8B5CF6" },
];

export default function FounderStory() {
  return (
    <section id="founder" data-testid="founder-story-section" className="py-24 sm:py-32 relative overflow-hidden bg-[#0B1B33]">
      <div className="absolute top-20 right-0 w-[26rem] h-[26rem] rounded-full bg-[#38BDF8]/15 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[24rem] h-[24rem] rounded-full bg-[#FF5722]/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#38BDF8]">The Operator Behind It</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mt-3 text-white">
            Built by someone who has walked your lots.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 mt-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative max-w-md">
              <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-[#38BDF8] to-[#FF5722] -rotate-3 scale-105 opacity-25" />
              <div className="relative rounded-[2.5rem] overflow-hidden border-2 border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
                <img
                  src="https://www.opera360trains.com/founder.jpg"
                  alt="Christopher Love, Founder of Opera360"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -right-4 bg-[#0F2440] rounded-2xl border-2 border-white/10 shadow-xl px-4 py-3 flex items-center gap-2 animate-floaty">
                <BadgeCheck className="w-4 h-4 text-[#10B981]" />
                <span className="text-xs font-bold text-white">Licensed Real Estate Professional</span>
              </div>
            </div>

            <blockquote className="mt-10 border-l-4 border-[#FF5722] pl-6">
              <p className="text-lg sm:text-xl text-slate-200 leading-relaxed italic">
                "Every module in Opera360 is a system I had to build by hand — fair-housing programs,
                onboarding tracks, SOP libraries, walk standards — across thousands of units. Now it's
                packaged so no operator has to rebuild it from tribal knowledge again."
              </p>
              <footer className="mt-4">
                <div className="font-display font-black text-lg text-white">Christopher Love</div>
                <div className="text-sm font-bold text-slate-400">Founder, Opera360</div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {["Fair Housing Program Builder", "Yardi · RentManager", "SOP Design"].map((t) => (
                    <span key={t} className="px-3 py-1 rounded-full bg-[#38BDF8]/10 border border-[#38BDF8]/20 text-[#7DD3FC] text-xs font-bold">{t}</span>
                  ))}
                </div>
              </footer>
            </blockquote>

            <div className="grid grid-cols-2 gap-4 mt-10">
              {STATS.map(([v, l], i) => (
                <motion.div
                  key={l}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  data-testid={`founder-stat-${i}`}
                  className="rounded-2xl border-2 border-white/10 bg-[#0F2440]/85 backdrop-blur p-5 shadow-[0_18px_50px_-20px_rgba(0,0,0,0.55)]"
                >
                  <div className="font-display font-black text-3xl text-white">{v}</div>
                  <div className="text-xs font-bold text-slate-400 mt-1 leading-snug">{l}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl border-2 border-white/10 bg-[#0F2440]/85 backdrop-blur p-7 sm:p-9 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.6)]"
          >
            <div className="font-display font-black text-xl mb-6 text-white">The road to Opera360</div>
            <div className="space-y-0">
              {TIMELINE.map((t, i) => (
                <motion.div
                  key={t.org}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  className="relative pl-8 pb-7 last:pb-0 border-l-2 border-white/10"
                >
                  <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full border-2 border-[#0F2440] shadow" style={{ background: t.color }} />
                  <div className="text-xs font-bold uppercase tracking-wider" style={{ color: t.color }}>{t.when}</div>
                  <div className="font-display font-bold text-base mt-0.5 text-white">{t.role} · <span className="text-slate-400">{t.org}</span></div>
                  <p className="text-sm text-slate-300 leading-relaxed mt-1">{t.note}</p>
                </motion.div>
              ))}
            </div>
            <a
              data-testid="founder-story-link"
              href="https://www.opera360trains.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#FF5722] text-white font-bold text-sm hover:bg-white hover:text-[#0B1B33] transition-colors duration-300"
            >
              Read the full story on opera360trains.com <ArrowUpRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
